/**
 * Owl-style product carousel — embla-carousel-driven.
 *
 *   Selector:  `.owl-carousel.product_recommendations_carousel`
 *   Behaviour: standard horizontal slider with native drag/touch support,
 *              dot navigation, and responsive items-per-view via CSS.
 *
 * No-op when no matching markup is present — safe to call against every
 * shadow root.
 */

import EmblaCarousel from 'embla-carousel'

export function initOwlPager(root: ShadowRoot | Document): void {
  const carousels = Array.from(root.querySelectorAll('.owl-carousel')) as HTMLElement[]
  if (carousels.length === 0) return
  for (const carousel of carousels) wireOwlPager(carousel)
}

/**
 * Wires the legacy `.owl-carousel` markup up to an embla-carousel instance.
 *
 * Embla expects three nested elements (root → viewport → container → slides);
 * we don't have that, so we wrap the existing children at runtime. The legacy
 * `.item` markup stays unchanged — it becomes the slide content. CSS for the
 * embla classes is injected into the shadow root once per render (idempotent).
 */
function wireOwlPager(carousel: HTMLElement): void {
  const slides = Array.from(carousel.children).filter(c => c instanceof HTMLElement) as HTMLElement[]
  if (slides.length === 0) return

  // Wrap children into embla's expected viewport + container structure.
  const container = document.createElement('div')
  container.className = 'evaro-embla__container'
  for (const slide of slides) {
    slide.classList.add('evaro-embla__slide')
    container.appendChild(slide)
  }
  const viewport = document.createElement('div')
  viewport.className = 'evaro-embla__viewport'
  viewport.appendChild(container)
  carousel.appendChild(viewport)
  carousel.classList.add('evaro-embla')

  // Dot row goes outside the viewport so it doesn't get dragged with the slides.
  const dotsRow = document.createElement('div')
  dotsRow.className = 'evaro-embla__dots'
  carousel.insertAdjacentElement('afterend', dotsRow)

  injectRuntimeStyles(carousel.getRootNode())

  const embla = EmblaCarousel(viewport, {
    align: 'start',
    containScroll: 'trimSnaps',
    loop: false,
    dragFree: false,
    skipSnaps: false,
  })

  const renderDots = () => {
    const snaps = embla.scrollSnapList()
    dotsRow.textContent = ''
    // Match owl's behaviour: hide the dot row when every slide already fits.
    if (snaps.length <= 1) {
      dotsRow.style.display = 'none'
      return
    }
    dotsRow.style.display = ''
    snaps.forEach((_, i) => {
      const dot = document.createElement('button')
      dot.type = 'button'
      dot.className = 'evaro-embla__dot'
      dot.setAttribute('aria-label', `Slide ${i + 1} of ${snaps.length}`)
      dot.addEventListener('click', () => embla.scrollTo(i))
      dotsRow.appendChild(dot)
    })
    syncActiveDot()
  }

  const syncActiveDot = () => {
    const selected = embla.selectedScrollSnap()
    Array.from(dotsRow.children).forEach((dot, i) => {
      if (i === selected) dot.classList.add('evaro-embla__dot--active')
      else dot.classList.remove('evaro-embla__dot--active')
    })
  }

  embla.on('init', renderDots)
  embla.on('reInit', renderDots)
  embla.on('select', syncActiveDot)
}

// Runtime styles for the embla wrappers we inject at hydration time. A separate
// build-time fallback grid lives at `packages/landings/src/legacy.ts`
// (CAROUSEL_FALLBACK_CSS) — it paints sensible defaults so the cards stack
// reasonably before this runtime CSS arrives. Keep both in sync visually.
const RUNTIME_STYLES = `
  .evaro-embla {
    position: relative;
    width: 100%;
  }
  .evaro-embla__viewport {
    overflow: hidden;
    width: 100%;
    /* Reserve the horizontal axis for the slider so mobile Safari doesn't steal
     * the gesture as ambient page scroll. */
    touch-action: pan-y;
  }
  .evaro-embla__container {
    display: flex;
    gap: 24px;
    /* Embla applies its transform here. Don't override transition; let the lib own it. */
  }
  .evaro-embla__slide {
    flex: 0 0 calc((100% - 72px) / 4);
    min-width: 0;
  }
  @media (max-width: 1280px) { .evaro-embla__slide { flex-basis: calc((100% - 48px) / 3); } }
  @media (max-width: 1060px) { .evaro-embla__slide { flex-basis: calc((100% - 24px) / 2); } }
  @media (max-width: 768px)  { .evaro-embla__slide { flex-basis: 100%; } }

  .evaro-embla__dots {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 16px;
  }
  .evaro-embla__dot {
    width: 10px;
    height: 10px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: currentColor;
    opacity: 0.35;
    cursor: pointer;
    transition: opacity 120ms ease, transform 120ms ease;
  }
  .evaro-embla__dot:hover { opacity: 0.6; }
  .evaro-embla__dot--active { opacity: 1; transform: scale(1.15); }
`

const STYLE_SENTINEL = 'data-evaro-carousel-rt'

function injectRuntimeStyles(rootNode: Node): void {
  const host = rootNode as ShadowRoot | Document
  // Don't double-inject if a previous carousel on the same root already added them.
  if ((host as ParentNode).querySelector?.(`style[${STYLE_SENTINEL}]`)) return
  const style = document.createElement('style')
  style.setAttribute(STYLE_SENTINEL, '')
  style.textContent = RUNTIME_STYLES
  const target = host instanceof ShadowRoot ? host : host.head
  target.appendChild(style)
}
